// TEN11-F1 (docs/build-briefs/TEN11-F1-console-open-as-support.md; TEN11 brief sections 4.2 and 7): the location side of
// "Open as support". A software owner's console tab opens this app at /support and hands it a one-time code through
// window.opener; the tab redeems it for a support session token that lives in sessionStorage only. Nothing here
// reads or writes the staff tablet configuration in localStorage, and no credential is ever in a URL.
//
// The screen elements (the Support session location and the ended screen) are built here at runtime, not authored in
// index.html, the way the console's own sheets are, so the shared chrome ratchet stays unchanged. Every shown value
// is set with textContent.

export const SUPPORT_PATH = "/support";
export const SUPPORT_TAB_STORAGE_KEY = "bms-support-tab";
/** How long the tab waits for the console to send the code. The mint is one round trip. */
export const HANDOFF_WAIT_MS = 10_000;
/** How often the location asks the server how long is left. The read never slides the idle window. */
export const STATUS_POLL_MS = 30_000;

export interface SupportTabState {
  token: string;
  tenantId: string;
  locationId: string;
  customer: string;
  locationName: string;
  operator: string;
  userId: string;
}

export interface SupportStatus {
  customer: string;
  locationName: string;
  operator: string;
  expiresAt: string;
}

export function isSupportPage(pathname: string): boolean {
  return pathname === SUPPORT_PATH || pathname === `${SUPPORT_PATH}/`;
}

/** The console adds ?fresh=<n> each time it opens a location, so a reload (no such query after the first load) resumes
 * the stored session while a new open always asks for a new code. */
export function isFreshOpen(search: string): boolean {
  return new URLSearchParams(search).has("fresh");
}

/** The location's name in a stored support tab: `locationName`, or the older key a tab stored before LOCNAME1-P4 holds
 * (DEC-434). Readers accept both forever. */
export function legacySupportTabName(stored: Readonly<Record<string, unknown>>): string | undefined {
  for (const key of ["locationName", "bar"]) {
    const value = stored[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return undefined;
}

export function loadSupportTab(
  storage: Storage = window.sessionStorage,
): SupportTabState | undefined {
  const raw = storage.getItem(SUPPORT_TAB_STORAGE_KEY);
  if (raw === null) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return undefined;
    const r = value as Record<string, unknown>;
    // A tab stored before LOCNAME1-P4 holds the location's name under its old key. Read it, and write the new one next save.
    const read = legacySupportTabName(r);
    const keys = ["token", "tenantId", "locationId", "customer", "operator", "userId"] as const;
    if (!keys.every((k) => typeof r[k] === "string" && (r[k] as string).length > 0))
      return undefined;
    if (read === undefined) return undefined;
    return {
      token: r["token"] as string,
      tenantId: r["tenantId"] as string,
      locationId: r["locationId"] as string,
      customer: r["customer"] as string,
      locationName: read,
      operator: r["operator"] as string,
      userId: r["userId"] as string,
    };
  } catch {
    return undefined;
  }
}

export function saveSupportTab(
  state: SupportTabState,
  storage: Storage = window.sessionStorage,
): void {
  storage.setItem(SUPPORT_TAB_STORAGE_KEY, JSON.stringify(state));
}

export function clearSupportTab(storage: Storage = window.sessionStorage): void {
  storage.removeItem(SUPPORT_TAB_STORAGE_KEY);
}

/** "42 min left", "1 min left", "Under 1 min left", "Ended". Rounds up so the location never says 0 while time is left. */
export function timeLeftText(expiresAtMs: number, nowMs: number): string {
  const ms = expiresAtMs - nowMs;
  if (!Number.isFinite(ms) || ms <= 0) return "Ended";
  const minutes = Math.ceil(ms / 60_000);
  return minutes <= 1 ? "Under 1 min left" : `${minutes} min left`;
}

/** Asks the console tab (window.opener) for the code and waits for it. Accepts a reply only from that window and
 * this origin. Resolves null when there is no opener, the console answers with an error, or time runs out. */
export function waitForHandoffCode(
  win: Window,
  timeoutMs: number = HANDOFF_WAIT_MS,
): Promise<string | null> {
  const opener = win.opener as Window | null;
  if (opener === null || opener === undefined || opener.closed) return Promise.resolve(null);
  const origin = win.location.origin;
  return new Promise((resolve) => {
    const done = (value: string | null): void => {
      win.clearTimeout(timer);
      win.removeEventListener("message", onMessage);
      resolve(value);
    };
    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== origin || event.source !== opener) return;
      const data: unknown = event.data;
      if (typeof data !== "object" || data === null) return;
      const message = data as { type?: unknown; handoffCode?: unknown };
      if (message.type === "support-code" && typeof message.handoffCode === "string") {
        done(message.handoffCode);
      } else if (message.type === "support-error") {
        done(null);
      }
    };
    const timer = win.setTimeout(() => done(null), timeoutMs);
    win.addEventListener("message", onMessage);
    opener.postMessage({ type: "support-ready" }, origin);
  });
}

export interface RedeemedSupport {
  token: string;
  /** SUPFRAN1: 'network' is a franchisor console session, which has no location (location.id and name are null). */
  kind: "location" | "network";
  tenant: { id: string; name: string };
  location: { id: string | null; name: string | null };
  actor: { id: string; displayName: string };
}

/** The one-time code for a session token. Null on any refusal (the server answers one generic 401). */
export async function redeemHandoff(
  apiBaseUrl: string,
  code: string,
): Promise<RedeemedSupport | null> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/support-access/handoff/redeem`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    });
  } catch {
    return null;
  }
  if (response.status !== 200) return null;
  const body = (await response.json()) as RedeemedSupport;
  return body;
}

export async function fetchSupportStatus(
  apiBaseUrl: string,
  token: string,
): Promise<{ kind: "ok"; status: SupportStatus } | { kind: "ended" } | { kind: "unknown" }> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/support-access/session`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { kind: "unknown" };
  }
  if (response.status === 401 || response.status === 404) return { kind: "ended" };
  if (response.status !== 200) return { kind: "unknown" };
  return { kind: "ok", status: (await response.json()) as SupportStatus };
}

/** TEN11-F3 (DEC-414): the tab is going away (closed, navigated away). A keepalive request outlives the page;
 * navigator.sendBeacon cannot carry the Authorization header, so it is not used. The server treats it as a pending end.
 * Its heartbeat timeout is the backstop when this never arrives (a crash, a lost network). */
export function sendSupportCloseNotice(apiBaseUrl: string, token: string): void {
  try {
    void fetch(`${apiBaseUrl}/support-access/session/end`, {
      method: "POST",
      keepalive: true,
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ reason: "disconnected" }),
    }).catch(() => undefined);
  } catch {
    // The heartbeat timeout ends it regardless.
  }
}

/** Best effort: the End control, and a new open replacing an older session in the same tab. */
export async function endSupportSessionRequest(apiBaseUrl: string, token: string): Promise<void> {
  try {
    await fetch(`${apiBaseUrl}/support-access/session/end`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    // The server ends it at its idle limit regardless.
  }
}

export interface SupportBanner {
  /** Updates the shown names and the time left. */
  show(status: SupportStatus): void;
  /** Starts the poll. `onEnded` runs once, when the server says the session is over. */
  watch(apiBaseUrl: string, token: string, onEnded: () => void): void;
  remove(): void;
}

/** The persistent Support session location: first child of the app shell, so every screen sits below it. */
export function mountSupportBanner(shell: HTMLElement, onEndClick: () => void): SupportBanner {
  const bar = document.createElement("div");
  bar.className = "support-bar";
  bar.id = "support-bar";
  bar.dataset["testid"] = "support-bar";
  bar.setAttribute("role", "status");
  const label = document.createElement("span");
  label.className = "support-bar-label";
  label.textContent = "Support session";
  const who = document.createElement("span");
  who.className = "support-bar-who";
  who.dataset["testid"] = "support-bar-customer";
  const left = document.createElement("span");
  left.className = "support-bar-left";
  left.dataset["testid"] = "support-bar-left";
  const end = document.createElement("button");
  end.type = "button";
  end.className = "btn-secondary support-bar-end";
  end.dataset["testid"] = "support-bar-end";
  end.textContent = "End session";
  end.addEventListener("click", onEndClick);
  bar.append(label, who, left, end);
  shell.prepend(bar);

  let expiresAtMs = 0;
  let poll: number | undefined;
  let tick: number | undefined;
  const render = (): void => {
    left.textContent = timeLeftText(expiresAtMs, Date.now());
  };
  const show = (status: SupportStatus): void => {
    who.textContent = `${status.customer}, ${status.locationName}`;
    expiresAtMs = Date.parse(status.expiresAt);
    render();
  };
  return {
    show,
    watch(apiBaseUrl, token, onEnded): void {
      tick = window.setInterval(render, 10_000);
      const check = async (): Promise<void> => {
        const result = await fetchSupportStatus(apiBaseUrl, token);
        if (result.kind === "ended") {
          onEnded();
        } else if (result.kind === "ok") {
          show(result.status);
        }
      };
      poll = window.setInterval(() => void check(), STATUS_POLL_MS);
      window.addEventListener("focus", () => void check());
      void check();
    },
    remove(): void {
      if (poll !== undefined) window.clearInterval(poll);
      if (tick !== undefined) window.clearInterval(tick);
      bar.remove();
    },
  };
}

/** The screen the tab shows when the session is over or never started. Covers the whole page. */
export function showSupportEnded(
  message: string,
  hintText: string = "Open this location again from Snip & Sip Admin.",
): void {
  document.getElementById("support-ended")?.remove();
  const screen = document.createElement("div");
  screen.className = "support-ended";
  screen.id = "support-ended";
  screen.dataset["testid"] = "support-ended";
  screen.setAttribute("role", "alert");
  const title = document.createElement("h1");
  title.className = "support-ended-title";
  title.textContent = message;
  const hint = document.createElement("p");
  hint.className = "support-ended-hint";
  hint.textContent = hintText;
  screen.append(title, hint);
  document.body.append(screen);
}
